import Phaser from 'phaser';
import { PowerUpInfo } from '../entities/PowerUp';

interface ActivePowerUp {
  info: PowerUpInfo;
  startTime: number;
  container: Phaser.GameObjects.Container;
  timerBar: Phaser.GameObjects.Graphics;
  icon: Phaser.GameObjects.Text;
  timeText: Phaser.GameObjects.Text;
}

export class PowerUpDisplay {
  private scene: Phaser.Scene;
  private activePowerUps: Map<string, ActivePowerUp> = new Map();
  private startX: number = 10;
  private startY: number = 80;
  private slotHeight: number = 45;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.scene.events.on('powerup-collected', (info: PowerUpInfo) => {
      this.addPowerUp(info);
    });
  }

  private addPowerUp(info: PowerUpInfo): void {
    // Remove existing power-up of same type if present
    if (this.activePowerUps.has(info.type)) {
      this.removePowerUp(info.type);
    }

    const index = this.activePowerUps.size;
    const y = this.startY + (index * this.slotHeight);

    // Create container
    const container = this.scene.add.container(this.startX, y);
    container.setDepth(1000);
    container.setScrollFactor(0);

    // Background panel
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x000000, 0.7);
    bg.fillRoundedRect(0, 0, 180, 40, 8);
    bg.lineStyle(2, info.color, 1);
    bg.strokeRoundedRect(0, 0, 180, 40, 8);
    container.add(bg);

    // Icon
    const icon = this.scene.add.text(10, 20, info.icon, {
      fontSize: '24px'
    });
    icon.setOrigin(0, 0.5);
    container.add(icon);

    // Name
    const name = this.scene.add.text(45, 12, info.name.replace('!', ''), {
      fontSize: '14px',
      color: `#${info.color.toString(16).padStart(6, '0')}`,
      fontStyle: 'bold'
    });
    name.setOrigin(0, 0.5);
    container.add(name);

    // Time remaining text
    const timeText = this.scene.add.text(45, 28, `${(info.duration / 1000).toFixed(1)}s`, {
      fontSize: '12px',
      color: '#FFFFFF'
    });
    timeText.setOrigin(0, 0.5);
    container.add(timeText);

    // Timer bar background
    const timerBg = this.scene.add.graphics();
    timerBg.fillStyle(0x333333, 0.8);
    timerBg.fillRoundedRect(45, 32, 125, 4, 2);
    container.add(timerBg);

    // Timer bar (will be updated)
    const timerBar = this.scene.add.graphics();
    container.add(timerBar);

    // Rarity stars
    const rarityStars = info.rarity === 'epic' ? '⭐⭐⭐' : info.rarity === 'rare' ? '⭐⭐' : '⭐';
    const stars = this.scene.add.text(155, 12, rarityStars, {
      fontSize: '10px'
    });
    stars.setOrigin(0, 0.5);
    container.add(stars);

    // Fade in animation
    container.setAlpha(0);
    this.scene.tweens.add({
      targets: container,
      alpha: 1,
      duration: 300,
      ease: 'Power2'
    });

    // Store active power-up
    const activePowerUp: ActivePowerUp = {
      info,
      startTime: Date.now(),
      container,
      timerBar,
      icon,
      timeText
    };

    this.activePowerUps.set(info.type, activePowerUp);

    // Schedule removal
    this.scene.time.delayedCall(info.duration, () => {
      this.removePowerUp(info.type);
    });
  }

  private removePowerUp(type: string): void {
    const powerUp = this.activePowerUps.get(type);
    if (!powerUp) return;

    // Fade out animation
    this.scene.tweens.add({
      targets: powerUp.container,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        powerUp.container.destroy();
        this.activePowerUps.delete(type);
        this.rearrangePowerUps();
      }
    });
  }

  private rearrangePowerUps(): void {
    let index = 0;
    this.activePowerUps.forEach((powerUp) => {
      const targetY = this.startY + (index * this.slotHeight);
      this.scene.tweens.add({
        targets: powerUp.container,
        y: targetY,
        duration: 300,
        ease: 'Power2'
      });
      index++;
    });
  }

  public update(): void {
    const now = Date.now();

    this.activePowerUps.forEach((powerUp) => {
      const elapsed = now - powerUp.startTime;
      const remaining = Math.max(0, powerUp.info.duration - elapsed);
      const remainingSeconds = remaining / 1000;
      const progress = remaining / powerUp.info.duration;

      // Update timer text
      powerUp.timeText.setText(`${remainingSeconds.toFixed(1)}s`);

      // Update timer bar
      powerUp.timerBar.clear();
      const barWidth = 125 * progress;
      let barColor = powerUp.info.color;

      // Change color based on remaining time
      if (progress < 0.3) {
        barColor = 0xFF0000; // Red when low
      } else if (progress < 0.6) {
        barColor = 0xFFAA00; // Orange when medium
      }

      powerUp.timerBar.fillStyle(barColor, 1);
      powerUp.timerBar.fillRoundedRect(45, 32, barWidth, 4, 2);

      // Pulse icon when time is low
      if (progress < 0.2 && Math.floor(now / 200) % 2 === 0) {
        powerUp.icon.setScale(1.2);
      } else {
        powerUp.icon.setScale(1);
      }
    });
  }

  public destroy(): void {
    this.activePowerUps.forEach((powerUp) => {
      powerUp.container.destroy();
    });
    this.activePowerUps.clear();
    this.scene.events.off('powerup-collected');
  }
}
